import { breakpointSizes } from 'components/utils/breakpoint'
import { getImageUrl } from 'lib/image/imageUtils'
import { clamp } from 'lib/maths/mathUtils'

import {
  MAPBOX_STATIC_IMAGE_BASE_URL,
  MAPBOX_STATIC_IMAGE_MAX_DIMENSION,
  MAPBOX_STATIC_IMAGE_MIN_DIMENSION,
  PROPERTY_MAP_COORDINATE_PRECISION,
  PROPERTY_MAP_DEFAULT_STATIC_IMAGE_HEIGHT,
  PROPERTY_MAP_DEFAULT_STATIC_MARKER_IMAGE_ID,
  PROPERTY_MAP_SINGLE_MARKER_ZOOM,
  PROPERTY_MAP_STATIC_AUTO_VIEWPORT_PADDING,
  PROPERTY_MAP_STATIC_MARKER_DEFAULT_DIMENSIONS,
  PROPERTY_MAP_STATIC_MARKER_IMAGE_DIMENSIONS_BY_ID,
} from '../constants'
import { getMarkerPin } from '../markerPins'

interface StaticMapUrlOptions {
  accessToken: string
  focusOnFirstMarker?: boolean
  height: number
  highlightedMarkerId?: string
  markerScale?: number
  markers: Array<App.MapMarker>
}

function formatCoordinate(value: number) {
  return Number(value.toFixed(PROPERTY_MAP_COORDINATE_PRECISION)).toString()
}

function getStaticMarkerImageId(marker: App.MapMarker) {
  const markerPin = getMarkerPin(marker.type)
  const markerImageId = markerPin.imageId ?? PROPERTY_MAP_DEFAULT_STATIC_MARKER_IMAGE_ID

  return markerImageId
}

function getStaticMarker(marker: App.MapMarker, highlightedMarkerId?: string, markerScale = 1) {
  const isActive = highlightedMarkerId === marker.id
  const markerImageId = getStaticMarkerImageId(marker)
  const markerImageDimensions = PROPERTY_MAP_STATIC_MARKER_IMAGE_DIMENSIONS_BY_ID[markerImageId] ?? PROPERTY_MAP_STATIC_MARKER_DEFAULT_DIMENSIONS
  const markerImageUrl = getImageUrl(isActive ? `${markerImageId}-active` : markerImageId, {
    width: Math.round(markerImageDimensions.width * markerScale),
    height: Math.round(markerImageDimensions.height * markerScale),
  })

  return `url-${encodeURIComponent(markerImageUrl)}(${formatCoordinate(marker.longitude)},${formatCoordinate(marker.latitude)})`
}

function getStaticMapOverlay(markers: Array<App.MapMarker>, highlightedMarkerId?: string, markerScale = 1) {
  return markers.map(marker => getStaticMarker(marker, highlightedMarkerId, markerScale)).join(',')
}

function getStaticMapViewport(markers: Array<App.MapMarker>, singleMarkerZoom: number, focusOnFirstMarker?: boolean) {
  const firstMarker = markers[0]
  if (!firstMarker) {
    return undefined
  }

  if (focusOnFirstMarker || markers.length === 1) {
    return `${formatCoordinate(firstMarker.longitude)},${formatCoordinate(firstMarker.latitude)},${singleMarkerZoom}`
  }

  return 'auto'
}

function normaliseStaticImageDimension(value: number) {
  return clamp(MAPBOX_STATIC_IMAGE_MIN_DIMENSION, Math.round(value), MAPBOX_STATIC_IMAGE_MAX_DIMENSION)
}

export function buildStaticMapUrls({
  accessToken,
  focusOnFirstMarker,
  height,
  highlightedMarkerId,
  markerScale = 1,
  markers,
}: StaticMapUrlOptions) {
  const overlay = getStaticMapOverlay(markers, highlightedMarkerId, markerScale)
  const viewport = getStaticMapViewport(markers, PROPERTY_MAP_SINGLE_MARKER_ZOOM, focusOnFirstMarker)

  if (!overlay || !viewport) {
    return undefined
  }

  const safeImageHeight = normaliseStaticImageDimension(Number.isFinite(height) ? height : PROPERTY_MAP_DEFAULT_STATIC_IMAGE_HEIGHT)
  const searchParams = new URLSearchParams({ access_token: accessToken })

  if (viewport === 'auto') {
    searchParams.set('padding', PROPERTY_MAP_STATIC_AUTO_VIEWPORT_PADDING.toString())
  }

  function buildStaticMapUrl(width: number) {
    const safeImageWidth = normaliseStaticImageDimension(width)

    return `${MAPBOX_STATIC_IMAGE_BASE_URL}/${overlay}/${viewport}/${safeImageWidth}x${safeImageHeight}?${searchParams.toString()}`
  }

  return {
    mobileUrl: buildStaticMapUrl(Math.round(breakpointSizes.mobile.max)),
    tabletUrl: buildStaticMapUrl(Math.round(breakpointSizes.tablet.max)),
    desktopUrl: buildStaticMapUrl(Math.round(breakpointSizes.desktop.max)),
    largeDesktopUrl: buildStaticMapUrl(Math.round(breakpointSizes.largeDesktop.max)),
  }
}
